import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MODAL, ModalService } from '../../../services/modal.service';
import { FidelityCardTypeEnum } from '../../../services/fidelity/fidelity';
import { FidelityService } from '../../../services/fidelity/fidelity.service';
import { PaytweakRequestInput } from '../../../services/fidelity/paytweak';
import { Subscribable } from '../../../stores/tunnel/model/subscribable';
import { CustomerState } from '../../../stores/tunnel/model/tunnel-data-state';
import { TunnelDataStore } from '../../../stores/tunnel/tunnel-data.store';
import { dateToString } from '../../../services/helpers/utils';
import { StatisticTypeEnum } from '../../../enum/statistic-type.enum';
import { environment } from 'src/environments/environment';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { TunnelNavigationStore } from '../../../stores/tunnel/tunnel-navigation.store';
import { CustomerService } from '../../../services/customer.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from '../../../services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-customer-info-fidelity',
  templateUrl: './customer-info-fidelity.component.html',
})
export class CustomerInfoFidelityComponent extends Subscribable implements OnInit, OnDestroy {
  protected isLoading: boolean = false;
  protected refreshLoading: boolean = false;
  protected minPointValue: number = 0;

  protected currentCustomer: CustomerState;
  @Input() overrideCreatePremiumCard: boolean = false;
  @Output() onCreatePremiumCardRequested: EventEmitter<void> = new EventEmitter<void>();

  public readonly loyaltyUrl = environment.castorama.loyalty;

  get isActive(): boolean {
    return (
      this.currentCustomer?.fidelity?.typeCard !== undefined &&
      this.currentCustomer.fidelity.typeCard !== FidelityCardTypeEnum.PREMIUMWAITING &&
      this.currentCustomer.fidelity.typeCard !== FidelityCardTypeEnum.AUCUNE
    );
  }

  constructor(
    private readonly modalService: ModalService,
    private readonly fidelityService: FidelityService,
    private readonly tunnelDataStore: TunnelDataStore,
    private readonly tunnelNavigationStore: TunnelNavigationStore,
    private readonly customerService: CustomerService,
    private readonly featureSettingsService: FeatureSettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    super.subscribe();
  }

  ngOnDestroy(): void {
    super.unsubscribe();
  }

  protected async createFidelityCard(input: { withPremium: boolean }): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    this.modalService.display(MODAL.CUSTOMER_INFO_FORM, {
      defaultData: {
        customer: this.currentCustomer.userData,
        address: this.currentCustomer.userData.address,
        marketingConsent: this.currentCustomer.userData.marketingConsent,
      },
      upgrade: input.withPremium,
      statisticType: StatisticTypeEnum.NEW_FREE_CARD_RECHERCHE_CLIENT,
    });
  }

  private logoutCustomerIfNeeded() {
    if (!this.tunnelNavigationStore.isTunnelValue) {
      this.customerService.logout();
    }
  }

  protected printCard() {
    this.logoutCustomerIfNeeded();
    if (!this.currentCustomer?.fidelity) return;

    this.modalService.display(MODAL.DISPLAY_BARCODE, {
      barcodeValue: this.currentCustomer.fidelity.cardNumber,
      title:
        'Carte de fidélité C-Casto' +
        (this.currentCustomer.fidelity.typeCard !== FidelityCardTypeEnum.GRATUITE ? ' Max' : ''),
    });
  }

  protected subscribeToObservables(): void {
    this.subscriptions.push(
      this.tunnelDataStore.getCurrentCustomer().subscribe(customer => {
        if (customer) {
          this.currentCustomer = customer;
        }
      }),
      this.tunnelDataStore.getFidelityLoading().subscribe(async fidelityLoading => {
        this.isLoading = fidelityLoading;
      })
    );
  }

  protected async createPremiumCard() {
    if (this.overrideCreatePremiumCard) {
      this.onCreatePremiumCardRequested.emit();
    } else {
      await this.createFidelityCard({
        withPremium: true,
      });
    }
  }

  protected displayRenewBtn(): boolean {
    if (!this.currentCustomer.fidelity?.expiredDate) return false;
    let today = new Date();
    let dateToNotice = new Date();

    const nbDaysNoticeEndSubscriptionPaidCard = 30;
    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(
      this.currentCustomer.fidelity.expiredDate.getTime() - nbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000
    );
    return today > dateToNotice;
  }

  protected async postRenewPremiumCard() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    try {
      await this.fidelityService.postRenewPremiumCard({
        idKingfisher: this.currentCustomer.userData.kfId,
      });
    } catch (error) {
      logError(error);
    } finally {
      await this.showPaytweakQrCode();
      this.isLoading = false;
    }
  }

  protected async activatePremiumWaitingCard() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    try {
      this.isLoading = true;
      await this.showPaytweakQrCode();
    } catch (error) {
      logError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private buildPaytweakRequestInput(): PaytweakRequestInput {
    return {
      customerId: this.currentCustomer.userData.kfId,
      cardNumber: this.currentCustomer.fidelity.cardNumber,
      firstname: this.currentCustomer.userData.firstname,
      lastname: this.currentCustomer.userData.lastname,
      address: this.currentCustomer.userData.address.line1,
      postalCode: this.currentCustomer.userData.address.postalCode,
      city: this.currentCustomer.userData.address.city,
      country: this.currentCustomer.userData.address.country,
      phone: this.currentCustomer.userData.phoneNumber,
      email: this.currentCustomer.userData.email,
    };
  }

  private async showPaytweakQrCode() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    let input: PaytweakRequestInput = this.buildPaytweakRequestInput();

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: input,
      onCloseCallback: undefined,
    });
  }

  //Permet d'ajuster la position de la bulle avec les points
  protected getSliderPosition(): number {
    const offset = 2;
    let val: number = (this.currentCustomer.fidelity.points / this.currentCustomer.fidelity.maxPoints) * 100 - offset;
    if (val + offset < 0) {
      val = -offset;
    } else if (val + offset > 89) {
      val = 89;
    }
    return val;
  }

  protected async refreshFidelity() {
    try {
      this.refreshLoading = true;
      await this.tunnelDataStore.loadCurrentCustomer(this.currentCustomer.userData);
    } catch (error) {
      logError(error);
    } finally {
      this.refreshLoading = false;
    }
  }

  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;
  protected readonly dateToString = dateToString;
  protected readonly faPrint = faPrint;
}
